import LoadError from "@/components/LoadError";
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, Trash2, Sparkles, Copy } from "lucide-react";
import { apiFetch } from "@/lib/api";
import { Button, Input, Textarea, Spinner, useToast } from "@/components/earnos/kit";
import { colors, statusColor } from "@/lib/theme";

const STATUSES = ["New", "Researching", "Draft ready", "Awaiting approval", "Contacted", "Replied", "Meeting", "Proposal sent", "Won", "Lost", "Paused"];
const CHANNELS = ["email", "dm", "text", "follow_up", "proposal"];
const TONES = ["friendly", "professional", "confident", "short"];
const CHANNEL_LABEL = { email: "Email", dm: "Direct Message", text: "Text", follow_up: "Follow-up", proposal: "Proposal" };

export default function LeadDetail() {
  const { id } = useParams();
  const isNew = id === "new";
  const navigate = useNavigate();
  const toast = useToast();
  const [loading, setLoading] = useState(!isNew);
  const [loadError, setLoadError] = useState("");
  const actionLock = useRef(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [form, setForm] = useState({ business_name: "", contact_name: "", contact_method: "", website: "", social: "", industry: "", location: "", source: "", estimated_value: "", status: "New", notes: "", next_follow_up: "" });
  const [channel, setChannel] = useState("email");
  const [tone, setTone] = useState("friendly");
  const [drafting, setDrafting] = useState(false);
  const [draft, setDraft] = useState("");

  const load = useCallback(async () => {
    if (isNew) { setLoading(false); setLoadError(""); return; }
    setLoading(true); setLoadError("");
    try { const l = await apiFetch(`/leads/${id}`); setForm({ ...l, estimated_value: l.estimated_value?.toString() || "" }); }
    catch (e) { setLoadError(e.message || "Could not load lead."); }
    finally { setLoading(false); }
  }, [id, isNew]);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => { setFormError(""); setForm((f) => ({ ...f, [k]: v })); };

  const save = async () => {
    if (!form.business_name.trim()) { setFormError("Business or customer name is required"); toast.show("Business or customer name is required", "error"); return; }
    if (form.estimated_value !== "" && (!Number.isFinite(Number(form.estimated_value)) || Number(form.estimated_value) < 0)) { setFormError("Estimated value must be a non-negative number"); return; }
    if (loadError || actionLock.current) return;
    actionLock.current = true; setSaving(true);
    const body = { ...form, business_name: form.business_name.trim(), estimated_value: form.estimated_value ? parseFloat(form.estimated_value) : null, next_follow_up: form.next_follow_up || null };
    try {
      if (isNew) await apiFetch("/leads", { method: "POST", body });
      else await apiFetch(`/leads/${id}`, { method: "PUT", body });
      toast.show("Lead saved", "success");
      navigate("/leads", { replace: true });
    } catch (e) {
      setFormError(e?.message || "Failed to save"); toast.show(e?.message || "Failed to save", "error");
    } finally {
      actionLock.current = false; setSaving(false);
    }
  };

  const remove = async () => {
    await apiFetch(`/leads/${id}`, { method: "DELETE" });
    toast.show("Lead deleted", "info");
    navigate(-1);
  };

  const generateDraft = async () => {
    setDrafting(true);
    try {
      const r = await apiFetch("/outreach/draft", { method: "POST", body: { lead_id: id, channel, tone } });
      setDraft(r.message);
    } catch (e) {
      toast.show(e?.message || "Could not draft", "error");
    } finally {
      setDrafting(false);
    }
  };

  const copyDraft = async () => {
    try { await navigator.clipboard.writeText(draft); toast.show("Message copied", "success"); } catch { toast.show("Copy failed", "error"); }
  };

  if (loading) return <Spinner className="py-24" />;
  if (loadError) return <LoadError title="Lead" message={loadError} onRetry={load} />;

  return (
    <div className="animate-fade-in max-w-2xl">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} aria-label="Back to leads" data-testid="lead-back" className="text-ink"><ChevronLeft size={26} /></button>
          <h1 className="text-base font-bold text-ink">{isNew ? "New Lead" : "Lead"}</h1>
        </div>
        {!isNew && <button onClick={remove} aria-label="Delete lead" data-testid="lead-delete" className="text-danger"><Trash2 size={22} /></button>}
      </div>

      {formError && <p role="alert" className="mb-4 rounded-xl border border-danger p-3 text-ink">{formError}</p>}
      <Input label="Business / customer name" value={form.business_name} onChange={(e) => set("business_name", e.target.value)} placeholder="e.g. Sunrise Cafe" testID="lead-name-input" />
      <Input label="Contact name" value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} placeholder="Optional" />
      <Input label="Industry" value={form.industry} onChange={(e) => set("industry", e.target.value)} placeholder="e.g. Hospitality" />
      <Input label="Contact method" value={form.contact_method} onChange={(e) => set("contact_method", e.target.value)} placeholder="Email, phone, Instagram..." />
      <Input label="Website / social" value={form.website} onChange={(e) => set("website", e.target.value)} placeholder="Optional" />
      <Input label="Estimated value ($)" type="number" value={form.estimated_value} onChange={(e) => set("estimated_value", e.target.value)} placeholder="e.g. 300" />

      <label className="block text-xs font-semibold text-ink-2 mb-2">Status</label>
      <div className="flex flex-wrap gap-2">
        {STATUSES.map((s) => {
          const active = form.status === s;
          const c = statusColor[s] || colors.brand;
          return (
            <button key={s} type="button" aria-pressed={active} disabled={saving} onClick={() => set("status", s)} className="h-9 px-4 rounded-full text-base font-semibold border transition-colors" style={active ? { backgroundColor: c + "33", borderColor: c, color: c } : { backgroundColor: colors.surfaceTertiary, borderColor: colors.border, color: colors.onSurfaceTertiary }}>{s}</button>
          );
        })}
      </div>

      <Textarea label="Notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Anything to remember" rows={3} className="mt-4" />

      <Button title={isNew ? "Add Lead" : "Save Changes"} onClick={save} loading={saving} full testID="lead-save" />

      {!isNew && (
        <div className="mt-8 border-t border-line pt-6">
          <h3 className="text-xl font-bold text-ink">AI Outreach Builder</h3>
          <p className="text-base text-ink-2 mt-1 leading-relaxed">EARNOS drafts a message you can edit before sending. Nothing is sent automatically.</p>

          <p className="text-xs font-bold uppercase tracking-wide text-ink-2 mt-5 mb-2">Channel</p>
          <div className="flex flex-wrap gap-2">
            {CHANNELS.map((c) => <button key={c} onClick={() => setChannel(c)} className={`h-9 px-4 rounded-full text-base font-semibold border ${channel === c ? "bg-brand border-brand-light text-white" : "bg-surface-3 border-line text-ink-3"}`}>{CHANNEL_LABEL[c]}</button>)}
          </div>
          <p className="text-xs font-bold uppercase tracking-wide text-ink-2 mt-4 mb-2">Tone</p>
          <div className="flex flex-wrap gap-2">
            {TONES.map((t) => <button key={t} onClick={() => setTone(t)} className={`h-9 px-4 rounded-full text-base font-semibold border ${tone === t ? "bg-brand border-brand-light text-white" : "bg-surface-3 border-line text-ink-3"}`}>{t[0].toUpperCase() + t.slice(1)}</button>)}
          </div>

          <Button title="Draft with EARNOS" icon={Sparkles} variant="secondary" onClick={generateDraft} loading={drafting} full className="mt-5" testID="draft-outreach" />

          {draft ? (
            <div className="mt-5 rounded-xl border border-line bg-surface-2 p-4">
              <p className="earnos-kicker mb-2">AI-generated · editable</p>
              <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={6} className="w-full rounded-xl border border-line bg-surface p-3 text-base text-ink outline-none focus:border-brand-light" />
              <Button title="Copy Message" icon={Copy} small onClick={copyDraft} className="mt-3" testID="copy-outreach" />
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
